/**
 * Memoria scurtă a shell-ului, în browser.
 *
 * Fiecare pagină își randează propriul `AppShell`, deci la FIECARE clic din meniu shell-ul se
 * montează din nou — iar meniul utilizatorului, comutatorul de firmă și clopoțelul își cereau
 * din nou datele: trei acțiuni de server, ~30 de interogări, plus „…” în locul numelui până
 * soseau (măsurat în cod, 19.09.2026). Datele astea nu se schimbă de la un clic la altul.
 *
 * Aici se ține ULTIMUL răspuns, cu termen scurt. Un apel în termen primește aceeași promisiune
 * (două componente montate deodată nu dublează cererea); după termen se cere din nou.
 *
 * ⛔ Memoria e a TAB-ULUI, nu a omului: la ieșirea din cont, pe ecranul de autentificare și la
 * schimbarea firmei se golește (`uitaTot`), altfel următorul om logat în același tab ar vedea
 * numele și firmele celui dinainte. Pe server nu se scrie niciodată — doar din efecte, care nu
 * rulează la randarea pe server — deci nu se poate amesteca între utilizatori.
 */

type Intrare = { la: number; p: Promise<unknown>; valoare?: unknown; gata: boolean };
const intrari = new Map<string, Intrare>();

export function memo<T>(cheie: string, termenMs: number, fn: () => Promise<T>): Promise<T> {
  const acum = Date.now();
  const e = intrari.get(cheie);
  if (e && acum - e.la < termenMs) return e.p as Promise<T>;
  const intrare: Intrare = { la: acum, p: Promise.resolve(), gata: false };
  intrare.p = fn().then(
    (v) => { intrare.valoare = v; intrare.gata = true; return v; },
    (err) => { if (intrari.get(cheie) === intrare) intrari.delete(cheie); throw err; },
  );
  intrari.set(cheie, intrare);
  return intrare.p as Promise<T>;
}

/** Ultima valoare sosită pentru cheie (oricât de veche) — starea inițială la re-montare, ca
 *  numele să nu clipească „…” la fiecare navigare. `undefined` pe server și la prima montare. */
export function ultimaValoare<T>(cheie: string): T | undefined {
  if (typeof window === "undefined") return undefined;
  const e = intrari.get(cheie);
  return e?.gata ? (e.valoare as T) : undefined;
}

/** Uită o cheie — după o acțiune care îi schimbă valoarea (ex. notificare citită). */
export function uita(cheie: string): void {
  intrari.delete(cheie);
}

/** Uită tot: ieșire din cont, ecranul de autentificare, schimbarea firmei. */
export function uitaTot(): void {
  intrari.clear();
}
